// PowerSync connection shared by every app. Reads sync down from the PowerSync service;
// local writes queue in PowerSync and are sent to the same sync-api REST endpoints as before.
import { UpdateType, type AbstractPowerSyncDatabase, type PowerSyncBackendConnector } from "@powersync/web";
import { getAuthToken } from "./auth-token.js";
import { syncApiUrl } from "./sync-api-url.js";
import type { CommitOrderBatchInput } from "./collections/sales.js";
import type { Order, Voucher } from "./schemas/index.js";
import { TABLES } from "./powersync-tables.js";
import { toApi } from "./powersync-schema.js";

let tableTicket: string | null = null;
/** Guest Portal: the proof that this phone scanned its table (POST /auth/guest-table). Every request carries it from then on: the sync token, reads, and uploaded writes. */
export function setTableTicket(ticket: string | null) {
  tableTicket = ticket;
}

/** The headers of a sync-api call made by this app, for the few places that call fetch themselves. */
export function apiHeaders(): Record<string, string> {
  const token = getAuthToken();
  return { "content-type": "application/json", ...(token ? { authorization: `Bearer ${token}` } : {}), ...(tableTicket ? { "x-table-ticket": tableTicket } : {}) };
}
const headers = apiHeaders;

/** `params`/`headers` go to GET /powersync/token only (Guest Portal: order_id, x-member-ticket). */
/**
 * Fired on `window` when sync-api refuses this app's token outright (401/403), as opposed to
 * being unreachable. For a paired till or kitchen display that means its pairing is no longer
 * valid (unpaired, deleted, or given a new code) and it must be paired again.
 */
export const SYNC_REJECTED_EVENT = "cliqsuite:sync-rejected";

/** Fired on `window` when sync-api refuses a change for good, so the app can say why it just flipped back. */
export const SAVE_REFUSED_EVENT = "cliqsuite:save-refused";

export function syncConnector(token: { params?: Record<string, string>; headers?: Record<string, string> } = {}): PowerSyncBackendConnector {
  return {
    async fetchCredentials() {
      if (!getAuthToken()) return null;
      const res = await fetch(syncApiUrl(`/powersync/token?${new URLSearchParams(token.params)}`), { headers: { ...headers(), ...token.headers } });
      if (res.status === 401 || res.status === 403) window.dispatchEvent(new Event(SYNC_REJECTED_EVENT));
      if (!res.ok) return null;
      return (await res.json()) as { endpoint: string; token: string };
    },

    async uploadData(db: AbstractPowerSyncDatabase) {
      const tx = await db.getNextCrudTransaction();
      if (!tx) return;
      for (const op of tx.crud) {
        const entry = TABLES[op.table as keyof typeof TABLES];
        if (!entry) throw new Error(`No sync-api route for local table "${op.table}"`);
        const [schema, base] = entry;
        const url = syncApiUrl(op.op === UpdateType.PUT ? base : `${base}/${op.id}`);
        const method = op.op === UpdateType.PUT ? "POST" : op.op === UpdateType.PATCH ? "PATCH" : "DELETE";
        const data = op.opData ? toApi(schema, op.opData) : {};
        const body = op.op === UpdateType.DELETE ? undefined : JSON.stringify(op.op === UpdateType.PUT ? { id: op.id, ...data } : data);
        // A network error or 5xx throws: PowerSync keeps the queue and retries. A 4xx means the
        // server refused this change for good (deleted row, lost permission); retrying can't fix it,
        // so it's dropped and the next sync puts the local row back to the server's version.
        const res = await fetch(url, { method, headers: headers(), body, signal: AbortSignal.timeout(30_000) });
        if (res.status >= 500) throw new Error(`sync-api ${method} ${url} → ${res.status}`);
        if (!res.ok) console.warn(`sync-api refused ${method} ${op.table}/${op.id}: ${res.status} ${await res.text().catch(() => "")}`);
        // Deleting what's already gone is what the user wanted. A refused print job (no printer set up,
        // most often) never touches the sale it prints, so it isn't a lost change to warn about.
        if (!res.ok && !(method === "DELETE" && res.status === 404) && op.table !== "print_jobs") window.dispatchEvent(new Event(SAVE_REFUSED_EVENT));
      }
      await tx.complete();
    },
  };
}

/**
 * A till's or kitchen display's sync token says which modules were on when it was made (buckets
 * `store_floor` and `store_stock` in powersync/sync-rules.yaml). When the business's modules change,
 * reconnect so the next token, and so the synced tables, follow at once instead of within the token's hour.
 */
export function reconnectWhenModulesChange(db: AbstractPowerSyncDatabase, connector: () => PowerSyncBackendConnector): void {
  let last = "";
  void (async () => {
    for await (const result of db.watch("SELECT installed_modules FROM businesses", [])) {
      const now = String(result.rows?._array[0]?.installed_modules ?? "");
      if (!now) continue;
      if (last && now !== last && db.connected) {
        await db.disconnect();
        await db.connect(connector());
      }
      last = now;
    }
  })();
}

/** POST/GET a sync-api JSON route with the app's auth token. Throws the server's `detail` (or `error` code) on failure. */
export async function api<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(syncApiUrl(path), {
    method: body === undefined ? "GET" : "POST",
    headers: headers(),
    body: body === undefined ? undefined : JSON.stringify(body),
    signal: AbortSignal.timeout(30_000),
  });
  const json = (await res.json().catch(() => ({}))) as T & { error?: string; detail?: string };
  if (!res.ok) throw new Error(json.detail ?? json.error ?? `sync-api ${path} responded ${res.status}`);
  return json;
}

/** Thrown by `commitOrder` only when the request never reached sync-api (offline, DNS, timeout).
 * Carries the exact body so a till can queue it and resend it byte for byte later. */
export class SyncApiNetworkError extends Error {
  constructor(
    cause: unknown,
    public requestBody: Record<string, unknown>,
  ) {
    super("Couldn't reach sync-api");
    this.name = "SyncApiNetworkError";
    this.cause = cause;
  }
}

/** One atomic order write (POST /sales/orders/commit). Idempotent by order id, so a retry with the same ids is safe. */
export async function commitOrder(input: CommitOrderBatchInput): Promise<{ order: Order | null; duplicate: boolean }> {
  const requestBody = {
    ...input,
    items: input.items.map((item) => ({ row: item.row, selections: item.selections ?? [] })),
  } as Record<string, unknown>;
  let res: Response;
  try {
    res = await fetch(syncApiUrl("/sales/orders/commit"), {
      method: "POST",
      headers: headers(),
      body: JSON.stringify(requestBody),
      signal: AbortSignal.timeout(30_000),
    });
  } catch (err) {
    throw new SyncApiNetworkError(err, requestBody);
  }
  if (!res.ok) throw new Error(`sync-api /sales/orders/commit responded ${res.status}: ${await res.text().catch(() => "")}`);
  const body = (await res.json()) as { order: Order | null; duplicate?: boolean };
  return { order: body.order, duplicate: body.duplicate ?? false };
}

/** Spends points, creates the voucher and bumps the reward count, all server-side. */
export function redeemReward(rewardId: string, memberTicket: string) {
  return api<{ ok: true; voucher: Voucher; pointsAfter: number }>(`/crm/rewards/${rewardId}/redeem`, { ticket: memberTicket });
}

// Migrated apps import everything from "@cliqsuite/db/powersync" so no Electric code lands in their bundle.
export { getAuthToken, setAuthToken } from "./auth-token.js";
export { syncApiUrl } from "./sync-api-url.js";
export { planLocked, type PlanFields } from "./subscription.js";
export { psTable, psSchema, fromRow, toApi } from "./powersync-schema.js";
export { TABLES, type TableName, type RowOf } from "./powersync-tables.js";
export type { CommitOrderBatchInput, CommitOrderItemInput } from "./collections/sales.js";
